import './supBilling.css';
import '../dashboard/dashboardTable/dashboardTable.css';
import { useCallback, useEffect, useState } from 'react';
import Pagination from '../../components/pagination/Pagination';
import ReactLoading from 'react-loading';
import EmptyPage from '../../components/empty-page/emptyPage';
import { standardDate } from '../../utils/formatDate';
import ToolTip from '../../components/tooltip/Tooltip';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supBillingsServiceConfig } from '../../utils/supportServiceConfig/supBillingsServiceConfig';

const MODE_TO_SERVICE = {
    t: 'trip',
    a: 'act',
    c: 'camp',
    g: 'gu'
};

const SupBilling = ({ orgData }) => {
    const { orgId, mode } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const serviceType = MODE_TO_SERVICE[mode];

    const urlStatus = searchParams.get('status') || 'completed';
    const urlApproval = searchParams.get('approval') || 'approved';
    const urlPage = Number(searchParams.get('page')) || 1;

    const [loading, setLoading] = useState(false);

    const limit = 10;
    const selectedOrg = orgId || '';
    const selectedStatus = urlStatus;
    const selectedApproval = urlApproval;
    const pageNumber = urlPage;
    const offSet = (urlPage - 1) * limit;

    const [billingData, setBillingData] = useState([]);

    const serviceBillingsConfig = supBillingsServiceConfig[serviceType];

    // camp: one row per stay booking
    const isCamp = serviceType === 'camp';
    const tableHeader = [
        isCamp ? 'Stay' : 'Package Name',
        isCamp ? 'Check-in' : 'Trip Date',
        isCamp ? 'Rooms' : 'Booked Slots',
        'Payout Date',
        'Total Amount',
        'Commission',
        'Amount Paid',
        'Action'
    ];

    const [totalCount, setTotalCount] = useState(0);

    const totalData = totalCount;
    const totalPages = Math.ceil(totalData / limit);

    const SERVICE_TO_MODE = {
        trip: 't',
        act: 'a',
        camp: 'c',
        gu: 'g'
    };

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }, []);

    const handleServiceType = (event) => {
        const selectedValue = event.target.value;
        const newMode = SERVICE_TO_MODE[selectedValue];

        navigate(`/support/${newMode}/billings?${searchParams.toString()}`);

        setBillingData([]);
    };

    const updateURL = useCallback(
        (params) => {
            const current = Object.fromEntries([...searchParams]);

            Object.keys(params).forEach((key) => {
                if (params[key] === '' || params[key] === null) {
                    delete current[key];
                } else {
                    current[key] = params[key];
                }
            });

            setSearchParams(current);
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        if (!totalPages) return;

        if (pageNumber > totalPages) {
            updateURL({ page: totalPages });
        }

        // eslint-disable-next-line
    }, [totalPages, pageNumber, updateURL]);

    const paginationClick = (number, offSetNumber) => {
        if (Math.ceil(totalData / limit) > 1) {
            updateURL({ page: number });

            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    };

    useEffect(() => {
        if (!selectedOrg || !selectedStatus) return;

        serviceBillingsConfig.fetchBillingCount({
            selectedOrg,
            setTotalCount,
            selectedStatus,
            selectedApproval,
            setLoading,
            serviceType
        });

        // eslint-disable-next-line
    }, [selectedOrg, selectedStatus, selectedApproval, serviceType]);

    useEffect(() => {
        if (!selectedOrg || !selectedApproval || !selectedStatus) return;

        const fetchBillingData = async () => {
            await serviceBillingsConfig.fetchBillingData({
                limit,
                offSet,
                selectedOrg,
                selectedStatus,
                selectedApproval,
                setBillingData,
                setLoading,
                serviceType
            });
        };

        fetchBillingData();

        // eslint-disable-next-line
    }, [selectedOrg, selectedStatus, selectedApproval, limit, offSet, serviceType]);

    /*
     * Get booked slots for all services.
     *
     * Trip may still have bookedSlots directly.
     * Guide / Activity / Camp can have slots[].bookedSlots.
     */
    const getBookedSlots = (row) => {
        // Old/direct field
        if (typeof row?.bookedSlots === 'number') {
            return row.bookedSlots;
        }

        // New slot-based structure
        if (Array.isArray(row?.slots)) {
            return row.slots.reduce((total, slot) => total + (Number(slot?.bookedSlots) || 0), 0);
        }

        return 0;
    };

    /*
     * Trip generally uses startDate.
     * Guide / Activity / Camp use date.
     */
    const getTripDate = (row) => {
        const date = row?.startDate || row?.date;

        if (!date) {
            return 'N/A';
        }

        return standardDate(date.split('T')[0]) || 'N/A';
    };

    return (
        <>
            {loading && (
                <div className='loadingBackground'>
                    <ReactLoading type='spin' color='#006aff' className='loadingSpin' />
                </div>
            )}

            <div className='billingContainer table-top-container'>
                <div className='alignCenter filter bookingSticky'>
                    <div className='orgBookingFilters'>
                        {/* SERVICE TYPE */}
                        <select
                            name='serviceType'
                            id='serviceType'
                            value={serviceType}
                            className='select mr-1'
                            onChange={handleServiceType}
                        >
                            <option value='trip'>Trip</option>
                            <option value='act'>Activity</option>
                            <option value='gu'>Guide</option>
                            <option value='camp'>Camp</option>
                        </select>

                        {/* ORGANIZER */}
                        <select
                            name='org'
                            id='org'
                            className='select'
                            value={selectedOrg}
                            onChange={(e) => {
                                if (e.target.value) {
                                    navigate(
                                        `/support/${mode}/billings/${e.target.value}?status=${selectedStatus}&approval=${selectedApproval}&page=1`
                                    );
                                }
                            }}
                        >
                            <option value=''>Select Organizer</option>

                            {orgData.map((org) => (
                                <option value={org._id} key={org._id}>
                                    {org.companyName}
                                </option>
                            ))}
                        </select>

                        {/* STATUS */}
                        <ToolTip
                            content={
                                <div className='tooltip-content'>
                                    <div>
                                        <strong>Generated:</strong> Payouts are generated, not paid yet
                                    </div>

                                    <div>
                                        <strong>Completed:</strong> Payouts are paid
                                    </div>

                                    <div>
                                        <strong>Not Eligible:</strong> Some problem in payouts, contact support
                                    </div>
                                </div>
                            }
                        >
                            <select
                                name='type'
                                id='type'
                                className='select'
                                value={selectedStatus}
                                onChange={(e) => {
                                    updateURL({
                                        status: e.target.value,
                                        page: 1
                                    });
                                }}
                            >
                                <option value='generated'>Generated</option>
                                <option value='completed'>Completed</option>
                                <option value='not-eligible'>Not Eligible</option>
                            </select>
                        </ToolTip>

                        {/* APPROVAL */}
                        <select
                            name='type'
                            id='type'
                            className='select'
                            value={selectedApproval}
                            onChange={(e) => {
                                updateURL({
                                    approval: e.target.value,
                                    page: 1
                                });
                            }}
                        >
                            <option value='approved'>Approved</option>
                            <option value='not-approved'>Not Approved</option>
                        </select>
                    </div>
                </div>

                {billingData?.length === 0 && !loading ? (
                    <EmptyPage />
                ) : (
                    <div className='tableContainer'>
                        {/* DESKTOP TABLE */}
                        <table className='table'>
                            <thead className='tableHead'>
                                <tr>
                                    {tableHeader.map((head, headIndex) => (
                                        <th key={headIndex}>{head}</th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody>
                                {billingData?.map((row, rowIndex) => (
                                    <tr key={rowIndex} className='tableBodyRow'>
                                        {/* Package Name */}
                                        <td>{row?.contentId?.title ?? 'N/A'}</td>

                                        {/* Trip / Activity / Guide / Camp Date */}
                                        <td>{getTripDate(row)}</td>

                                        {/* Booked Slots */}
                                        <td>{getBookedSlots(row)}</td>

                                        {/* Payout Date */}
                                        <td>
                                            {row?.payoutDetails?.paymentDate
                                                ? standardDate(row.payoutDetails.paymentDate.split('T')[0]) || 'N/A'
                                                : 'N/A'}
                                        </td>

                                        {/* Total Amount */}
                                        <td>₹ {row?.payoutDetails?.totalAmount ?? '0'}</td>

                                        {/* Commission */}
                                        <td>₹ {row?.payoutDetails?.commission ?? '0'}</td>

                                        {/* Amount Paid */}
                                        <td>₹ {row?.payoutDetails?.amount ?? '0'}</td>

                                        {/* Action */}
                                        <td>NA</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* MOBILE */}
                        <div className='responsiveCardContainer'>
                            {billingData?.map((row, rowIndex) => (
                                <div key={rowIndex} className='responsiveCard'>
                                    <div className='cardContent'>
                                        <p>
                                            <strong>{tableHeader[0]}:</strong> {row?.contentId?.title ?? 'N/A'}
                                        </p>

                                        <p>
                                            <strong>{tableHeader[1]}:</strong> {getTripDate(row)}
                                        </p>

                                        <p>
                                            <strong>{tableHeader[2]}:</strong> {getBookedSlots(row)}
                                        </p>

                                        <p>
                                            <strong>{tableHeader[3]}:</strong>{' '}
                                            {row?.payoutDetails?.paymentDate
                                                ? standardDate(row.payoutDetails.paymentDate.split('T')[0]) || 'N/A'
                                                : 'N/A'}
                                        </p>

                                        <p>
                                            <strong>{tableHeader[4]}:</strong> ₹{' '}
                                            {row?.payoutDetails?.totalAmount ?? '0'}
                                        </p>

                                        <p>
                                            <strong>{tableHeader[5]}:</strong> ₹ {row?.payoutDetails?.commission ?? '0'}
                                        </p>

                                        <p>
                                            <strong>{tableHeader[6]}:</strong> ₹ {row?.payoutDetails?.amount ?? '0'}
                                        </p>

                                        <div className='sendMessageBtn'>
                                            <button className='actBtn payoutBtn noWidth'>Bill Generation</button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <Pagination
                    paginationClick={paginationClick}
                    numberOfRows={totalData}
                    offSet={offSet}
                    pageNumber={pageNumber}
                    limit={limit}
                />
            </div>
        </>
    );
};

export default SupBilling;
